// Wizard mode for Brand Genius - step-by-step guided setup for empty fields
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Tables } from '../../../database.types'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { AutoAwesome } from '@mui/icons-material'
import fieldMetadata from '@/lib/fieldMetadata.json'
import { ChevronLeft, ChevronRight, Sparkles, LayoutGrid, AlertCircle } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import AIGenerateDialog from '@/components/brand/AIGenerateDialog'
import { RainbowButton } from '@/components/ui/rainbow-button'
import { ButtonGroup, ButtonGroupItem } from '@/components/ui/button-group'
import { BrandProgress } from './BrandProgress'
import { Card, CardContent } from '@/components/ui/card'
import { LoadingSpinner } from '@/components/ui/loading-spinner'

type FieldMetadata = {
  hidden: boolean
  description: string
  example: string | number
  bestPractices: string
  group: string
  inputControl: string
  options: string[]
  keyField: boolean
  allowAiGeneration: boolean
  howItsUsed: string
  name: string
}

type Brand = Tables<'brands'>

interface BrandGeniusWizardProps {
  localCurrentBrand: Brand | null;
  setLocalCurrentBrand: React.Dispatch<React.SetStateAction<Brand | null>>;
  updateBrandField: (brandId: string, field: string, value: string) => Promise<void>;
  onSwitchToFormMode?: () => void;
}

export function BrandGeniusWizard({ 
  localCurrentBrand, 
  setLocalCurrentBrand, 
  updateBrandField,
  onSwitchToFormMode
}: BrandGeniusWizardProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [emptyFields, setEmptyFields] = useState<[string, FieldMetadata][]>([])
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [currentAIField, setCurrentAIField] = useState<keyof Brand>('brand_name');
  const [isFeedbackMode, setIsFeedbackMode] = useState(false);
  const [brandGeniusFeedback, setBrandGeniusFeedback] = useState('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  
  const { register, setValue, watch } = useForm<any>({
    defaultValues: {},
    mode: 'onChange'
  })

  // Watch current field for changes
  const currentField = emptyFields[currentStep];
  const currentFieldKey = currentField ? currentField[0] as keyof Brand : null;
  const currentFieldValue = currentFieldKey ? watch(currentFieldKey) : null;

  // Update brand data when current field changes (for UI display only)
  useEffect(() => {
    if (currentFieldKey && currentFieldValue !== undefined && currentFieldValue !== null && localCurrentBrand) {
      // Clear any previous save errors when user starts typing
      setSaveError(null);
      
      // Update local brand state for UI display only
      const updatedBrand = { 
        ...localCurrentBrand,
        [currentFieldKey]: currentFieldValue 
      };
      setLocalCurrentBrand(updatedBrand);
    }
  }, [currentFieldValue, currentFieldKey, localCurrentBrand, setLocalCurrentBrand]);

  // Initialize fields list (show all fields in wizard mode)
  useEffect(() => {
    if (localCurrentBrand) {
      const fields = Object.entries(fieldMetadata)
        .filter(([key, meta]) => {
          // Show all fields except hidden ones and status
          return !meta.hidden && key !== 'status';
        })
        .sort((a, b) => a[1].group.localeCompare(b[1].group))
      setEmptyFields(fields)
      
      // Reset to first step if needed
      if (fields.length > 0 && currentStep >= fields.length) {
        setCurrentStep(0);
      }
    }
  }, [localCurrentBrand, currentStep])

  // Initialize form values when brand loads
  useEffect(() => {
    if (localCurrentBrand && emptyFields.length > 0) {
      // Set form values for all fields
      Object.keys(fieldMetadata).forEach((key) => {
        setValue(key as keyof Brand, localCurrentBrand[key as keyof Brand] || '');
      });
    }
  }, [localCurrentBrand, emptyFields, setValue]);

  // Clear save errors when step changes
  useEffect(() => {
    setSaveError(null);
  }, [currentStep]);

  // Handle next step
  const nextStep = async () => {
    if (!localCurrentBrand) return;
    
    const currentFieldName = emptyFields[currentStep][0] as keyof Brand;
    const currentValue = watch(currentFieldName);
    
    if (!currentValue || (typeof currentValue === 'string' && currentValue.trim() === '')) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Create updated brand object
      const updatedBrand = {
        ...localCurrentBrand,
        [currentFieldName]: currentValue
      };
      
      // Save the current field to database
      await updateBrandField(localCurrentBrand.id, currentFieldName, String(currentValue));
      
      console.log(`Successfully saved field: ${currentFieldName} = ${currentValue}`);

      // Update local state with saved values
      setLocalCurrentBrand(updatedBrand);
      
      // Check if this was the final step
      const isFinalStep = currentStep >= emptyFields.length - 1;
      
      if (isFinalStep) {
        // We've completed the wizard - set completion state
        console.log('Wizard completed! Setting completion state...');
        setIsCompleted(true);
        setIsSaving(false);
        return;
      }

      // Move to next field (simple advancement since we're in review mode)
      const nextStepIndex = currentStep + 1;

      // Set form value for the next field
      if (nextStepIndex < emptyFields.length) {
        const nextFieldName = emptyFields[nextStepIndex][0] as keyof Brand;
        const nextValue = updatedBrand[nextFieldName];
        if (nextValue !== undefined) {
          setValue(nextFieldName, nextValue);
        }
      }

      setCurrentStep(nextStepIndex);
      setIsSaving(false);
    } catch (error) {
      console.error('Error updating brand field:', error);
      setIsSaving(false);
      setSaveError(`Failed to save ${currentFieldName}. Please try again.`);
    }
  }

  const prevStep = () => {
    if (currentStep > 0) {
      // Save current field value to state
      const currentFieldName = emptyFields[currentStep][0] as keyof Brand;
      const currentValue = watch(currentFieldName);
      if (currentValue && localCurrentBrand) {
        const updatedBrand = {
          ...localCurrentBrand,
          [currentFieldName]: currentValue
        };
        setLocalCurrentBrand(updatedBrand);
      }

      // Move to previous step
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);

      // Set the form value for the previous field
      const prevFieldName = emptyFields[prevStep][0] as keyof Brand;
      const prevValue = localCurrentBrand?.[prevFieldName];
      if (prevValue !== undefined) {
        setValue(prevFieldName, prevValue);
      }
    }
  }

  // Focus input field when step changes
  useEffect(() => {
    // Get the current field's input element
    const input = document.querySelector(`[name="${emptyFields[currentStep]?.[0]}"]`) as HTMLElement;
    if (input) {
      // Small delay to ensure the DOM has updated
      setTimeout(() => {
        input.focus();
      }, 100);
    }
  }, [currentStep, emptyFields]);

  // Show completion message if explicitly completed
  if (isCompleted) {
    return (
      <div className="flex justify-center">
        <Card className="w-full max-w-2xl shadow-lg">
          <CardContent className="p-8 text-center">
            <BrandProgress brand={localCurrentBrand} showLink={false} />
            <div className="mt-6 space-y-4">
              <h2 className="text-2xl font-bold text-green-600 mb-2">🎉 Brand Profile Complete!</h2>
              <p className="text-muted-foreground">
                Your brand profile is ready. You can now use Form Mode to make any updates or refinements.
              </p>
              <div className="flex justify-center pt-4">
                <Button
                  onClick={onSwitchToFormMode}
                  className="flex items-center gap-2"
                  size="lg"
                >
                  <LayoutGrid className="h-4 w-4" />
                  Switch to Form Mode
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentFieldMetadata = emptyFields[currentStep];
  if (!currentFieldMetadata || !localCurrentBrand) return null;

  const [fieldName, metadata] = currentFieldMetadata;
  const fieldKey = fieldName as keyof Brand;

  return (
    <div className="flex justify-center">
      <Card className="w-full max-w-2xl shadow-lg">
        <CardContent className="p-8 space-y-6">
          <form onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Form submission prevented');
            return false;
          }}>
            {/* Progress indicator */}
            <div className="mb-6">
              <BrandProgress brand={localCurrentBrand} showLink={false} />
              <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
                <span>Field {currentStep + 1} of {emptyFields.length}</span>
                <span>Reviewing all fields</span>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-2">
                {metadata.name}
              </h2>
              <p className="text-muted-foreground">
                {metadata.howItsUsed}
              </p>
            </div>

            <div className="space-y-4">
              {saveError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Save Error</AlertTitle>
                  <AlertDescription>{saveError}</AlertDescription>
                </Alert>
              )}

              {metadata.inputControl === 'Textarea' ? (
                <Textarea
                  {...register(fieldKey)}
                  placeholder={metadata.example.toString()}
                  className="min-h-[200px]"
                  disabled={isSaving}
                  onKeyDown={(e) => {
                    // For textarea, only proceed if Ctrl+Enter or Cmd+Enter is pressed
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !isSaving) {
                      e.preventDefault();
                      nextStep();
                    }
                  }}
                />
              ) : metadata.options && metadata.options.length > 0 ? (
                <ButtonGroup 
                  value={watch(fieldKey) || ''} 
                  onValueChange={async (value) => {
                    if (!isSaving && localCurrentBrand) {
                      setValue(fieldKey, value);
                      setIsSaving(true);
                      setSaveError(null);
                      try {
                        // Create updated brand object
                        const updatedBrand = {
                          ...localCurrentBrand,
                          [fieldKey]: value
                        };
                        
                        // Save to database
                        await updateBrandField(localCurrentBrand.id, fieldKey, value);
                        console.log(`Auto-saved field: ${fieldKey} = ${value}`);
                        
                        // Update local state
                        setLocalCurrentBrand(updatedBrand);
                        
                      } catch (error) {
                        console.error('Error auto-saving field:', error);
                        setSaveError(`Failed to save ${fieldKey}. Please try again.`);
                      } finally {
                        setIsSaving(false);
                      }
                    }
                  }}
                  className="w-full"
                >
                  {metadata.options.map((option) => (
                    <ButtonGroupItem 
                      key={option} 
                      value={option}
                      className="flex-1"
                      disabled={isSaving}
                    >
                      {option}
                    </ButtonGroupItem>
                  ))}
                </ButtonGroup>
              ) : (
                <Input
                  {...register(fieldKey)}
                  type="text"
                  placeholder={metadata.example.toString()}
                  disabled={isSaving}
                  onKeyDown={(e) => {
                    console.log('Key pressed:', e.key, 'Event:', e);
                    // Prevent any form submission behavior
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Enter prevented');
                    }
                  }}
                  onChange={(e) => {
                    console.log('Input changed:', e.target.value);
                    // Let react-hook-form handle the change
                    register(fieldKey).onChange(e);
                  }}
                />
              )}

              {metadata.bestPractices && (
                <Alert>
                  <AlertTitle>Best Practices</AlertTitle>
                  <AlertDescription>
                    {metadata.bestPractices}
                  </AlertDescription>
                </Alert>
              )}

              {metadata.allowAiGeneration && (
                <RainbowButton
                  onClick={() => {
                    if (isSaving) return;
                    setCurrentAIField(fieldKey);
                    const currentValue = watch(fieldKey);
                    
                    if (currentValue && String(currentValue).trim() !== '') {
                      // If the field already has a value, show the choice modal
                      setShowChoiceModal(true);
                    } else {
                      // Otherwise show the AI dialog directly
                      setIsFeedbackMode(false);
                      setShowAIDialog(true);
                    }
                  }}
                  className="w-full"
                  disabled={isSaving}
                >
                  <AutoAwesome className="mr-2" />
                  Ask Brand Genius
                </RainbowButton>
              )}
            </div>

            <div className="flex justify-between pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 0 || isSaving}
              >
                <ChevronLeft className="mr-2" size={16} />
                Previous
              </Button>
              <Button
                type="button"
                onClick={nextStep}
                disabled={!watch(fieldKey) || (typeof watch(fieldKey) === 'string' && watch(fieldKey).trim() === '') || isSaving}
                className="min-w-[120px]"
              >
                {isSaving ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    {currentStep === emptyFields.length - 1 ? 'Complete' : 'Next'}
                    <ChevronRight className="ml-2" size={16} />
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {showAIDialog && (
        <AIGenerateDialog
          fieldName={currentAIField}
          brand={localCurrentBrand}
          defaultInstructions={metadata.bestPractices}
          fieldMetadata={metadata}
          existingValue={isFeedbackMode ? watch(currentAIField) : undefined}
          initialFeedback={isFeedbackMode ? brandGeniusFeedback : ''}
          onApply={(field: keyof Brand, content: string) => {
            setValue(field, content);
            // Update local brand state immediately but don't auto-advance
            setLocalCurrentBrand(prev => prev ? { ...prev, [field]: content } : null);
            setShowAIDialog(false);
            setIsFeedbackMode(false);
            setBrandGeniusFeedback('');
            // Don't call nextStep() here - let user review and manually proceed
          }}
          onClose={() => {
            setShowAIDialog(false);
            setIsFeedbackMode(false);
            setBrandGeniusFeedback('');
          }}
        />
      )}
      
      {/* Choice Modal when field already has a value */}
      {showChoiceModal && (
        <Dialog open={showChoiceModal} onOpenChange={setShowChoiceModal}>
          <DialogContent className="sm:max-w-[500px] p-6 bg-white">
            <DialogHeader>
              <DialogTitle>How would you like Brand Genius to help?</DialogTitle>
              <DialogDescription>
                Choose how you want Brand Genius to assist with this field.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <Button 
                onClick={() => {
                  setShowChoiceModal(false);
                  setIsFeedbackMode(false);
                  setShowAIDialog(true);
                }}
                className="w-full"
              >
                <AutoAwesome className="mr-2" />
                Have Brand Genius generate a new answer for me
              </Button>
              
              <Button 
                onClick={() => {
                  setShowChoiceModal(false);
                  setIsFeedbackMode(true);
                  setBrandGeniusFeedback('');
                  setShowAIDialog(true);
                }}
                variant="outline"
                className="w-full"
              >
                <Sparkles className="mr-2" />
                Give Brand Genius feedback to improve the current answer
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}